import { FileText, TestTube, Users, Beaker, LogOut, ChevronsUpDown, User, Building2, ShieldCheck, BookOpen, Bug } from "lucide-react"
import { useState } from "react"
import { useUserRol, useTienePermiso } from "../App"
import { ROL_LABELS } from "../lib/permisos"
import { useSidebar } from "./ui/sidebar";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "./ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"


interface User {
  name: string
  email: string
  avatar: string
}

interface AppSidebarProps {
  activeSection: string
  onSectionChange: (section: string) => void
  user: User
  onLogout: () => void
}

export function AppSidebar({ activeSection, onSectionChange, user, onLogout }: AppSidebarProps) {
  useSidebar();
  const rol = useUserRol();
  const puedeVerContactos = useTienePermiso('ver_contactos');

  const allItems = [
    { title: "Pedidos",          url: "#pedidos",   icon: FileText,    visible: true },
    { title: "Lista de Pruebas", url: "#pruebas",   icon: TestTube,    visible: true },
    { title: "Clientes",         url: "#clientes",  icon: Building2,   visible: puedeVerContactos },
    { title: "Tickets",          url: "#tickets",   icon: Bug,         visible: true },
    { title: "Usuarios",         url: "#usuarios",  icon: Users,       visible: rol === 'ADMIN' },
    { title: "Permisos",         url: "#permisos",  icon: ShieldCheck, visible: rol === 'ADMIN' },
    { title: "Documentación",    url: "#documentacion", icon: BookOpen, visible: true },
  ]
  const items = allItems.filter(item => item.visible);

  const getInitials = (name: string) => {
    if (!name?.trim()) return '?';
    return name.trim().split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const rolLabel = ROL_LABELS[rol] || 'Analista';

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-sidebar-primary">
            <Beaker className="h-4 w-4 text-sidebar-primary-foreground" />
          </div>
          <div>
            <h2 className="text-base font-medium text-sidebar-foreground">DEXTERITE</h2>
            <p className="text-xs text-sidebar-foreground/70">Laboratorios</p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Gestión</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={activeSection === item.url.substring(1)}
                    onClick={() => onSectionChange(item.url.substring(1))}
                  >
                    <a href={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-sidebar-accent transition-colors outline-none">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-primary text-sidebar-primary-foreground font-semibold text-xs">
                {getInitials(user.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-sidebar-foreground truncate leading-tight">
                  {user.name || 'Usuario'}
                </p>
                <p className="text-xs text-sidebar-foreground/60 truncate leading-tight">
                  {user.email}
                </p>
              </div>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-sidebar-foreground/40" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="top"
            align="start"
            className="w-64 mb-1"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex items-center gap-3 py-1">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold text-sm">
                  {getInitials(user.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{user.name || 'Usuario'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    rol === 'ADMIN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {rolLabel}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onLogout}
              className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
